import { useState, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export interface UserRole {
  role_name: string
  role_description: string
}

export interface UserPermission {
  permission_name: string
}

export interface AuthState {
  user: User | null
  loading: boolean
  userRoles: UserRole[]
  userPermissions: UserPermission[]
  hasRole: (roleName: string) => boolean
  hasPermission: (permissionName: string) => boolean
  hasAnyRole: (roleNames: string[]) => boolean
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  isModerator: () => boolean
  isEditor: () => boolean
  refreshRoles: () => Promise<void>
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [userRoles, setUserRoles] = useState<UserRole[]>([])
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([])

  // Cargar roles y permisos del usuario
  const loadUserRolesAndPermissions = async (userId: string) => {
    console.log('🔐 Loading roles for user:', userId)
    
    if (!userId) {
      console.log('❌ No userId provided to loadUserRolesAndPermissions')
      return
    }

    try {
      // Obtener roles del usuario
      console.log('🔍 Calling get_user_roles RPC...')
      const { data: rolesData, error: rolesError } = await supabase
        .rpc('get_user_roles', { user_uuid: userId })

      console.log('📋 Roles response:', { rolesData, rolesError })

      if (rolesError) {
        console.error('❌ Error loading user roles:', rolesError)
        // No asignar roles en caso de error para evitar problemas en la UI
        setUserRoles([])
      } else {
        setUserRoles(rolesData || [])
        console.log('✅ Roles loaded successfully:', rolesData?.length || 0)
      }

      // Obtener permisos del usuario
      console.log('🔍 Calling get_user_permissions RPC...')
      const { data: permissionsData, error: permissionsError } = await supabase
        .rpc('get_user_permissions', { user_uuid: userId })

      console.log('🔐 Permissions response:', { permissionsData, permissionsError })

      if (permissionsError) {
        console.error('❌ Error loading user permissions:', permissionsError)
        // No asignar permisos en caso de error para evitar problemas en la UI
        setUserPermissions([])
      } else {
        setUserPermissions(permissionsData || [])
        console.log('✅ Permissions loaded successfully:', permissionsData?.length || 0)
      }
    } catch (error) {
      console.error('💥 Exception in loadUserRolesAndPermissions:', error)
      // En caso de error, asignar arrays vacíos para evitar errores de UI
      setUserRoles([])
      setUserPermissions([])
    }
  }

  // Inicializar autenticación
  useEffect(() => {
    // Verificar sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadUserRolesAndPermissions(session.user.id)
      }
      setLoading(false)
    })

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadUserRolesAndPermissions(session.user.id)
      } else {
        setUserRoles([])
        setUserPermissions([])
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Función para verificar si el usuario tiene un rol específico
  const hasRole = (roleName: string): boolean => {
    if (!user || !userRoles.length) return false
    return userRoles.some(role => role.role_name === roleName)
  }

  // Función para verificar si el usuario tiene un permiso específico
  const hasPermission = (permissionName: string): boolean => {
    if (!user || !userPermissions.length) return false
    // Si tiene permiso ALL, tiene todos los permisos
    if (userPermissions.some(perm => perm.permission_name === 'ALL')) {
      return true
    }
    return userPermissions.some(perm => perm.permission_name === permissionName)
  }

  // Función para verificar si el usuario tiene alguno de los roles especificados
  const hasAnyRole = (roleNames: string[]): boolean => {
    if (!user || !userRoles.length) return false
    return roleNames.some(roleName => hasRole(roleName))
  }

  // Funciones auxiliares para roles comunes
  const isAdmin = (): boolean => {
    if (!user || !userRoles.length) return false
    return hasAnyRole(['admin', 'superadmin'])
  }
  const isSuperAdmin = (): boolean => hasRole('superadmin')
  const isModerator = (): boolean => hasAnyRole(['moderator', 'admin', 'superadmin'])
  const isEditor = (): boolean => hasAnyRole(['editor', 'admin', 'superadmin'])

  // Función para refrescar roles (útil después de cambios)
  const refreshRoles = async () => {
    if (user) {
      await loadUserRolesAndPermissions(user.id)
    }
  }

  return {
    user,
    loading,
    userRoles,
    userPermissions,
    hasRole,
    hasPermission,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    isModerator,
    isEditor,
    refreshRoles
  }
}
